import React, { useEffect, useCallback, useState, useRef } from "react";
import { array, bool, object, string } from "prop-types";
import get from "lodash/get";
import { useSelector } from "react-redux";
import { StoryElementCard } from "../../../arrow/components/Molecules/StoryElementCard";
import { Paywall } from "../../../arrow/components/Rows/Paywall";
import AdComponent from "../../molecules/AdComponent";
import MoreAboutAuthors from "../../../arrow/components/Atoms/MoreAboutAuthors";
import { useDeviceType } from "../../customHooks/useDeviceType";
import "./story-elements.m.css";
import { FollowUsIcons } from "../../molecules/FollowUsIcons";

const TABOOLA_SCROLL_THRESHOLD_DESKTOP = 300;
const TABOOLA_SCROLL_THRESHOLD_MOBILE = 400;

const RenderCards = ({ story, cards, recommendedStories, disableRecommendedStories }) => {
  const member = useSelector((state) => get(state, ["member"], null));
  const isMagazineStory = story["story-template"] === "magazine";
  const isStoryBehindPaywall = !member && isMagazineStory;
  const isDesktop = useDeviceType() === "desktop";
  const storyHeadline = get(story, ["headline"], "");
  const storyMetadata = get(story, ["metadata"], "");

  const { creatorYoutubeCards } = cards.reduce(
    (acc, card) => {
      const cardTypes = get(card, ["metadata", "attributes", "cardtype"], []);

      if (Array.isArray(cardTypes) && cardTypes.includes("Creator Youtube")) {
        acc.creatorYoutubeCards.push(card);
      }

      return acc;
    },
    { creatorYoutubeCards: [] }
  );

  const creatorYoutubeVideos = creatorYoutubeCards.flatMap((card) =>
    get(card, ["story-elements"], []).filter((el) => el.type === "youtube-video" && el["embed-url"])
  );

  const getYoutubeVideoId = (url = "") => {
    try {
      if (url.includes("embed")) {
        return url.split("/embed/")[1].split("?")[0];
      }
      if (url.includes("watch")) {
        return new URL(url).searchParams.get("v");
      }
    } catch (e) {
      return null;
    }
    return null;
  };

  const getYoutubeThumbnail = (videoId) => `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;

  const socialLinks = {
    facebook: get(storyMetadata, ["creator-facebook-handle"], "").trim(),
    twitter: get(storyMetadata, ["creator-twitter-handle"], "").trim(),
    youtube: get(storyMetadata, ["creator-youtube-handle"], "").trim(),
    instagram: get(storyMetadata, ["creator-instagram-handle"], "").trim(),
  };

  const hasAllSocialLinks = Object.values(socialLinks).some(Boolean);

  const Ad = useCallback(
    (props) => (
      <AdComponent
        {...props}
        recommendedStories={recommendedStories}
        isDesktop={isDesktop}
        story={story}
        disableRecommendedStories={disableRecommendedStories}
      />
    ),
    [disableRecommendedStories, recommendedStories, isDesktop, story?.id]
  );

  return (
    <>
      {isStoryBehindPaywall ? (
        <>
          <StoryElementCard
            story={story}
            card={cards[0]}
            key={get(cards[0], ["id"], "")}
            recommendedStories={recommendedStories}
            adComponent={Ad}
          />
          <Paywall magazine={true} />
        </>
      ) : (
        cards.map((card, index) => (
          <div key={index}>
            <StoryElementCard card={card} story={story} recommendedStories={recommendedStories} adComponent={Ad} />
          </div>
        ))
      )}
      {hasAllSocialLinks && (
        <div styleName="social-container">
          <div styleName="inline-video-heading">{storyHeadline}&apos;s Social Presence</div>

          <ul>
            {socialLinks.facebook && (
              <li>
                <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer">
                  <i styleName="fbicon" /> Facebook
                </a>
              </li>
            )}

            {socialLinks.twitter && (
              <li>
                <a href={socialLinks.twitter} target="_blank" rel="noopener noreferrer">
                  <i styleName="xicon" /> X
                </a>
              </li>
            )}

            {socialLinks.youtube && (
              <li>
                <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer">
                  <i styleName="youtubeicon" /> YouTube
                </a>
              </li>
            )}

            {socialLinks.instagram && (
              <li>
                <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer">
                  <i styleName="instaicon" /> Instagram
                </a>
              </li>
            )}
          </ul>
        </div>
      )}

      {!isStoryBehindPaywall && creatorYoutubeVideos.length > 0 && (
        <div styleName="creator-video-container">
          <div styleName="creator-video-containerIn">
            <div styleName="inline-video-heading">Popular Content from {storyHeadline}</div>
            <div styleName="creator-video-outer">
              {creatorYoutubeVideos.map((video, index) => {
                const videoId = getYoutubeVideoId(video["embed-url"] || video.url);

                if (!videoId) return null;

                return (
                  <div styleName="creator-video-box" key={video.id || index}>
                    <a href={`https://www.youtube.com/watch?v=${videoId}`} target="_blank" rel="noopener noreferrer">
                      <div styleName="inline-frame-box">
                        <i styleName="youtube-icon" />
                        <img src={getYoutubeThumbnail(videoId)} alt="YouTube video thumbnail" loading="lazy" />
                      </div>
                    </a>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

RenderCards.propTypes = {
  story: object,
  cards: array,
  recommendedStories: array,
  disableRecommendedStories: bool,
};

const RenderStoryFooter = ({ story, taboola }) => {
  const qtState = useSelector((state) => state);
  const member = useSelector((state) => get(state, ["member"], null));
  const isMagazineStory = story["story-template"] === "magazine";
  const isStoryBehindPaywall = !member && isMagazineStory;
  const isAdfreePage = get(qtState, ["qt", "config", "subscriber"]) === "true";

  if (!isAdfreePage && isStoryBehindPaywall) {
    return null;
  }

  return (
    <>
      {/* {!isWebView && <StoryTags tags={story.tags} template="noBorder" story={story} />} */}

      <MoreAboutAuthors authors={story.authors} story={story} />

      {taboola && (
        <>
          <div id="taboola-below-article-thumbnails" className="TABOOLA"></div>
          <div
            dangerouslySetInnerHTML={{
              __html: `
                  <script type="text/javascript">
                    window._taboola = window._taboola || [];
                    _taboola.push({
                      mode: 'alternating-thumbnails-a',
                      container: 'taboola-below-article-thumbnails',
                      placement: 'Below Article Thumbnails',
                      target_type: 'mix'
                    });
                  </script>
              `,
            }}
          />
        </>
      )}
    </>
  );
};

RenderStoryFooter.propTypes = {
  story: object,
  taboola: bool,
};

const StoryElements = ({
  story,
  cards,
  recommendedStories,
  config,
  isContinueReadingPreview,
  disableRecommendedStories,
  isPerpetualStory,
  storyInstanceId,
}) => {
  const qtState = useSelector((state) => state);
  const [ClientEnhancers, setClientEnhancers] = useState(null);
  const isAdfreePage = get(qtState, ["qt", "config", "subscriber"]) === "true";
  const taboola = get(config, ["publisher-attributes", "taboola"], true);
  const isWebView = get(qtState, ["qt", "config", "isWebView"]) === true;
  const isCreatorStory = get(story, ["story-template"], "") === "creator";
  const isMagazineStory = story["story-template"] === "magazine";
  const [hasReachedTaboolaScrollThreshold, setHasReachedTaboolaScrollThreshold] = useState(false);
  const initialScrollYRef = useRef(0);
  const contentRef = useRef(null);
  const socialLinks = get(qtState, ["qt", "config",  "social-links"], {});

  useEffect(() => {
    let isMounted = true;

    import("./client-enhancers").then((module) => {
      if (isMounted) {
        setClientEnhancers(() => module.default);
      }
    });

    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    initialScrollYRef.current = window.scrollY;

    const handleScroll = () => {
      const isMobile = window.innerWidth < 768;
      const scrollThreshold = isMobile ? TABOOLA_SCROLL_THRESHOLD_MOBILE : TABOOLA_SCROLL_THRESHOLD_DESKTOP;
      const scrolledDistance = Math.abs(window.scrollY - initialScrollYRef.current);

      if (scrolledDistance >= scrollThreshold) {
        setHasReachedTaboolaScrollThreshold(true);
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    if (hasReachedTaboolaScrollThreshold && taboola === true && !isCreatorStory && !isWebView && !isAdfreePage) {
      window._taboola = window._taboola || [];
      window._taboola.push({ article: "auto" });

      if (!document.getElementById("tb_loader_script")) {
        const s = document.createElement("script");
        s.async = true;
        s.src = "//cdn.taboola.com/libtrc/jambojar-fortuneindia/loader.js";
        s.id = "tb_loader_script";

        const f = document.getElementsByTagName("script")[0];
        f.parentNode.insertBefore(s, f);
      }

      if (window.performance && typeof window.performance.mark === "function") {
        window.performance.mark("tbl_ic");
      }
    }
  }, [hasReachedTaboolaScrollThreshold, isAdfreePage, isCreatorStory, isWebView, taboola]);

  return (
    <div
      id="story-wrapper"
      ref={contentRef}
      className={isCreatorStory ? "container" : ""}
      styleName="story-elements-wrapper"
    >
      <div styleName={`${isCreatorStory ? "creator-content" : "cards-wrapper"} ${isMagazineStory ? "nomrg" : ""}`}>
        <RenderCards
          story={story}
          cards={cards}
          recommendedStories={recommendedStories}
          disableRecommendedStories={disableRecommendedStories}
        />
        {ClientEnhancers && (
          <ClientEnhancers
            rootRef={contentRef}
            story={story}
            isContinueReadingPreview={isContinueReadingPreview}
            isPerpetualStory={isPerpetualStory}
            storyInstanceId={storyInstanceId}
          />
        )}
        {!isContinueReadingPreview && (
          <>
            <RenderStoryFooter story={story} taboola={taboola} />
          </>
        )}
        {!isCreatorStory && isPerpetualStory === false && (
          <div styleName="followus-wrapper">
            <div styleName="followus-text">Follow Fortune India
              <small>News, insights and conversations that matter, on your favourite platforms.</small>
            </div>
            <ul styleName="followus-icons">
              <FollowUsIcons
                socialLinks={socialLinks}
                noIcon={false}
                customIcons={{
                  "follow-whatsapp": {
                    label: "Whatsapp",
                    svg: "",
                  },
                  "follow-facebook": {
                    label: "Facebook",
                    svg: (
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.69.24 2.69.24v2.97h-1.52c-1.49 0-1.96.93-1.96 1.88v2.26h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07z" />
                      </svg>
                    ),
                  },
                  "follow-x": {
                    label: "Twitter",
                    svg: (
                      <svg
                        width="18"
                        styleName="twtclass"
                        height="18"
                        viewBox="1.25 2.25 20.25 19.5"
                        aria-hidden="true"
                      >
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                      </svg>
                    ),
                  },
                  "follow-youtube": {
                    label: "YouTube",
                    svg: (
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path d="M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.6A3 3 0 0 0 .5 6.2 31.2 31.2 0 0 0 0 12a31.2 31.2 0 0 0 .5 5.8 3 3 0 0 0 2.1 2.1c1.9.6 9.4.6 9.4.6s7.5 0 9.4-.6a3 3 0 0 0 2.1-2.1A31.2 31.2 0 0 0 24 12a31.2 31.2 0 0 0-.5-5.8zM9.6 15.9V8.1l6.5 3.9-6.5 3.9z" />
                      </svg>
                    ),
                  },
                  "follow-instagram": {
                    label: "Instagram",
                    svg: (
                      <svg
                        styleName="instaclass"
                        width="18"
                        height="18"
                        viewBox="3 3 18 18"
                        aria-hidden="true"
                      >
                        <rect x="3" y="3" width="18" height="18" rx="5" />
                        <circle cx="12" cy="12" r="4" />
                        <circle cx="17.4" cy="6.6" r="0.6" fill="#fff" />
                      </svg>
                    ),
                  },
                  "follow-linkedin": {
                    label: "LinkedIn",
                    svg: (
                      <svg height="18" width="18" viewBox="0 0 382 382" fill="#000000" stroke="#000000">
                        <g id="SVGRepo_bgCarrier" stroke-width="0" />
                        <g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round" />
                        <g id="SVGRepo_iconCarrier"> <path d="M347.445,0H34.555C15.471,0,0,15.471,0,34.555v312.889C0,366.529,15.471,382,34.555,382h312.889 C366.529,382,382,366.529,382,347.444V34.555C382,15.471,366.529,0,347.445,0z M118.207,329.844c0,5.554-4.502,10.056-10.056,10.056 H65.345c-5.554,0-10.056-4.502-10.056-10.056V150.403c0-5.554,4.502-10.056,10.056-10.056h42.806 c5.554,0,10.056,4.502,10.056,10.056V329.844z M86.748,123.432c-22.459,0-40.666-18.207-40.666-40.666S64.289,42.1,86.748,42.1 s40.666,18.207,40.666,40.666S109.208,123.432,86.748,123.432z M341.91,330.654c0,5.106-4.14,9.246-9.246,9.246H286.73 c-5.106,0-9.246-4.14-9.246-9.246v-84.168c0-12.556,3.683-55.021-32.813-55.021c-28.309,0-34.051,29.066-35.204,42.11v97.079 c0,5.106-4.139,9.246-9.246,9.246h-44.426c-5.106,0-9.246-4.14-9.246-9.246V149.593c0-5.106,4.14-9.246,9.246-9.246h44.426 c5.106,0,9.246,4.14,9.246,9.246v15.655c10.497-15.753,26.097-27.912,59.312-27.912c73.552,0,73.131,68.716,73.131,106.472 L341.91,330.654L341.91,330.654z" /> </g>
                      </svg>
                    ),
                  },
                  "follow-google": {
                    label: "Google",
                    svg: (
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                        style={{ display: "block", margin: 0, padding: 0 }}
                      >
                        <path
                          fill="#000"
                          d="M21.35 12.27c0-.79-.07-1.55-.2-2.27H12v4.3h5.24a4.48 4.48 0 0 1-1.94 2.94v2.45h3.14c1.84-1.69 2.91-4.18 2.91-7.42z"
                        />
                        <path
                          fill="#000"
                          d="M12 21.75c2.63 0 4.84-.87 6.45-2.36l-3.14-2.45c-.87.58-1.98.92-3.31.92-2.54 0-4.7-1.72-5.47-4.03H3.28v2.53A9.75 9.75 0 0 0 12 21.75z"
                        />
                        <path
                          fill="#000"
                          d="M6.53 13.83A5.86 5.86 0 0 1 6.22 12c0-.64.11-1.26.31-1.83V7.64H3.28A9.75 9.75 0 0 0 2.25 12c0 1.57.38 3.05 1.03 4.36l3.25-2.53z"
                        />
                        <path
                          fill="#000"
                          d="M12 6.14c1.43 0 2.71.49 3.72 1.46l2.79-2.79C16.84 3.27 14.63 2.25 12 2.25a9.75 9.75 0 0 0-8.72 5.39l3.25 2.53C7.3 7.86 9.46 6.14 12 6.14z"
                        />
                      </svg>
                    ),
                  }
                }}
              />
            </ul>
        </div>
        )}
      </div>
    </div>
  );
};

StoryElements.propTypes = {
  config: object,
  story: object,
  cards: array,
  recommendedStories: array,
  isContinueReadingPreview: bool,
  disableRecommendedStories: bool,
  isPerpetualStory: bool,
  storyInstanceId: string,
};

export default StoryElements;
